import React, {
  useCallback,
  useEffect,
  useMemo, useRef, useState,
} from 'react';

import { Form, TextArea } from '@mairistem/composants';

import { toast } from 'react-toastify';
import {
  Bordereau,
  CodeServiceTva,
  EntiteExercice,
  Exercice,
  Mouvement,
  ParametreGeneralEntite,
  useService,
} from '@mairistem/comptabilite';
import { isNewIdentifiant } from '@mairistem/tools';
import { DropdownOption } from '@mairistem/liste';
import { Combobox, isHorizon } from '@mairistem/finances-utils';
import AUTO from '../../../../constants/common/common';
import BaseProfilEcritureMouvement from
  '../../../../classes/mouvements/profilEcriture/Base/BaseProfilEcritureMouvement';
import BordereauModal from './BordereauModal';
import { GroupeProfilEcriture } from '../../../../constants/mouvements/groupesProfilEcriture';
import getCodesServiceTva from '../../../../services/parametres/codesServiceTva';
import { getDureeFromTiersOrExercice } from '../Complements/DelaiPaiement/delaiPaiementUtils';
import StorageKey from '../../../../constants/common/storageKey';
import BordereauSelect from '../../../Liquidation/LiquidationTreeView/Bordereau/BordereauSelect';
import CODE_BHM from '../../../../constants/mouvements/codeBHM';
import ProfilEcritureSearchInput from './ProfilEcritureSearchInput';

interface InformationsGeneralesProps {
  groupeProfilEcriture: GroupeProfilEcriture;
  identifiant: number | string;
  isDirty: React.MutableRefObject<boolean>;
  mouvement: Mouvement;
  onChange: any;
  onChangeProfilEcriture: (codeProfilEcriture: string) => void;
  onClear: (codeProfilEcriture: string) => void;
  profilEcriture: BaseProfilEcritureMouvement;
}

const InformationsGenerales = ({
  groupeProfilEcriture,
  identifiant,
  isDirty,
  mouvement,
  onChange,
  onChangeProfilEcriture,
  onClear,
  profilEcriture,
}: InformationsGeneralesProps) => {
  const [codesServiceTva, setCodesServiceTva] = useState<DropdownOption[]>([]);
  const [open, setOpen] = useState(false);
  const resolveCreateBordereau = useRef<((bordereau: Bordereau) => void) | null>(null);

  const [ServiceCombobox, isServiceComboboxVisible] = useService();

  const loadCodesServiceTva = async () => {
    try {
      const { data } = await getCodesServiceTva();
      setCodesServiceTva(data.map((code: CodeServiceTva) => ({
        'data-testid': `codeServiceTva${code?.codeServiceTva}`,
        key: code?.identifiant,
        text: `${code?.codeServiceTva} - ${code.libelle}`,
        value: code?.identifiant,
      })));
    } catch {
      toast.error('Erreur lors du chargement des codesServiceTva');
    }
  };

  useEffect(() => {
    loadCodesServiceTva();
  }, []);

  // Mettre a jour le session storage selon le dernier budget séléctionné
  useEffect(() => {
    if (mouvement?.exercice?.entite?.identifiantEntite && mouvement?.exercice?.identifiant) {
      sessionStorage.setItem(StorageKey.IDENTIFIANT_ENTITE, String(mouvement?.exercice?.entite?.identifiantEntite));
      sessionStorage.setItem(StorageKey.IDENTIFIANT_EXERCICE, String(mouvement?.exercice?.identifiant));
    }
  }, [mouvement?.exercice?.entite?.identifiantEntite, mouvement?.exercice?.identifiant]);

  const handleSelectBudget = useCallback((entite: ParametreGeneralEntite, exercice: Exercice) => {
    onChange({
      ...getDureeFromTiersOrExercice(exercice, mouvement?.tiers),
      identifiantExercice: exercice?.identifiant,
      identifiantEntite: entite?.identifiantEntite,
      exercice: {
        ...exercice,
        entite,
      },
    });
  }, [mouvement?.tiers, onChange]);

  const handleChangeCodeServiceTva = useCallback((e, { name, value }) => {
    onChange({ [name]: value });
  }, [onChange]);

  const handleSelectServiceUtilisateur = useCallback((identifiantService: number) => {
    onChange({ identifiantService });
  }, [onChange]);

  const handleChange = useCallback((e, { name, value }) => {
    onChange({ [name]: value });
  }, [onChange]);

  const renderBudget = useMemo(() => (
    <Form.Field
      control={EntiteExercice}
      disabled={!isNewIdentifiant(identifiant)}
      // @ts-ignore
      entite={mouvement?.exercice?.entite ?? {}}
      exercice={mouvement?.exercice ?? {}}
      label="Budget"
      onSelect={handleSelectBudget}
      width={7}
    />
  ), [
    handleSelectBudget,
    identifiant,
    mouvement?.exercice,
  ]);

  const renderProfilEcriture = useMemo(() => (
    <Form.Field
      // @ts-expect-error
      mouvement={mouvement}
      codeProfil={profilEcriture?.code}
      control={ProfilEcritureSearchInput}
      entite={mouvement?.exercice?.entite ?? {}}
      groupeProfilEcriture={groupeProfilEcriture}
      identifiant={identifiant}
      isDirty={isDirty}
      onChange={onChangeProfilEcriture}
      onClear={onClear}
      sens={mouvement?.sens ?? ''}
      width={7}
    />
  ), [
    groupeProfilEcriture,
    identifiant,
    isDirty,
    mouvement,
    onChangeProfilEcriture,
    onClear,
    profilEcriture?.code,
  ]);

  const renderServiceUtilisateur = useMemo(() => (
    <Form.Field
      control={ServiceCombobox}
      /* @ts-expect-error */
      identifiantService={mouvement?.identifiantService}
      label="Service utilisateur"
      onSelect={handleSelectServiceUtilisateur}
      required
      width={5}
    />
  ), [
    ServiceCombobox,
    handleSelectServiceUtilisateur,
    mouvement?.identifiantService,
  ]);

  const renderServiceTva = useMemo(() => (
    <Form.Field
      control={Combobox}
      data-testid="identifiantCodeServiceTva"
      label="Service TVA"
      // @ts-expect-error
      name="identifiantCodeServiceTva"
      clearable
      onChange={handleChangeCodeServiceTva}
      options={codesServiceTva}
      placeholder="Hors service assujetti"
      selection
      value={mouvement?.identifiantCodeServiceTva ?? ''}
      width={isHorizon() ? 9 : 4}
    />
  ), [
    codesServiceTva,
    handleChangeCodeServiceTva,
    mouvement?.identifiantCodeServiceTva,
  ]);

  const renderMouvementNumero = useMemo(() => (
    <Form.Input
      data-testid="identifiantCodeServiceTva"
      label="Numéro"
      placeholder={AUTO}
      readOnly
      value={mouvement?.numero ?? ''}
      width={2}
    />
  ), [mouvement?.numero]);

  const renderObjet = useMemo(() => (
    <Form.Field
      id="objetPiece"
      // @ts-expect-error TextArea
      control={TextArea}
      data-testid="objetMouvement"
      label="Objet"
      maxLength={100}
      name="objetPiece"
      onChange={handleChange}
      rows={5}
      value={mouvement?.objetPiece ?? ''}
      width={7}
    />
  ), [handleChange, mouvement?.objetPiece]);

  const renderReferencePiecesJustificatives = useMemo(() => (
    <Form.Field
      // @ts-expect-error TextArea
      control={TextArea}
      data-testid="referencePiecesJustificatives"
      label="Référence des pièces justificatives"
      maxLength={250}
      name="pieceJointe"
      onChange={handleChange}
      rows={5}
      value={mouvement?.pieceJointe ?? ''}
      width={9}
    />
  ), [handleChange, mouvement?.pieceJointe]);

  const handleClose = (data) => {
    setOpen(false);
    if (resolveCreateBordereau.current) {
      resolveCreateBordereau.current(data);
      resolveCreateBordereau.current = null;
    }
  };

  const handleCreateBordereau = (): Promise<Bordereau> => {
    setOpen(true);
    // Retourner une Promise qui se résout quand la modale se ferme
    return new Promise((resolve) => {
      resolveCreateBordereau.current = resolve;
    });
  };

  const handleChangeBordereau = useCallback((bordereau: Bordereau) => {
    onChange({
      bordereau,
      identifiantBordereau: bordereau?.identifiant,
      ...(mouvement?.identifiantBordereau !== bordereau?.identifiant ? { numero: null } : {}),
    });
  }, [mouvement?.identifiantBordereau, onChange]);

  return (
    <>
      <Form>
        <Form.Group>
          {renderBudget}
          <BordereauSelect
            identifiant={identifiant}
            mouvement={mouvement}
            onChange={handleChangeBordereau}
            onCreateBordereau={handleCreateBordereau}
            profilEcriture={profilEcriture}
            value={mouvement?.identifiantBordereau ?? -1}
            data-testid="identifiantBordereau"
            width={7}
            label="Bordereau"
            disabled={mouvement?.codeBHM === CODE_BHM.VALUE.HISTORISE}
          />
          {renderMouvementNumero}
        </Form.Group>
        <Form.Group>
          {renderProfilEcriture}
          {renderServiceTva}
          {(!isHorizon() && isServiceComboboxVisible) && renderServiceUtilisateur}
        </Form.Group>
        <Form.Group>
          {renderObjet}
          {renderReferencePiecesJustificatives}
        </Form.Group>
      </Form>
      <BordereauModal
        bordereau={{
          sens: mouvement?.sens,
          codeBHM: mouvement?.codeBHM,
          piecesCount: 0,
        }}
        entite={mouvement?.exercice?.entite}
        exercice={mouvement?.exercice}
        isCreation
        numeroDisabled
        onClose={handleClose}
        open={open}
      />
    </>
  );
};

export default InformationsGenerales;
